import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { HandoverChain, HandOffRequest } from '../models/handover-chain.model';
import {
  Testdatenblatt,
  TestdatenblattActivity,
  TestdatenblattCreateRequest,
  TestdatenblattUpdateRequest,
  TestdatenblattUsage,
} from '../models/testdatenblatt.model';

@Injectable({ providedIn: 'root' })
export class TestdatenblattService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/v1/projects`;

  private url(projectId: string, id?: string): string {
    const root = `${this.base}/${projectId}/testdatenblaetter`;
    return id ? `${root}/${id}` : root;
  }

  list(projectId: string): Observable<Testdatenblatt[]> {
    return this.http.get<Testdatenblatt[]>(this.url(projectId));
  }

  getById(projectId: string, id: string): Observable<Testdatenblatt> {
    return this.http.get<Testdatenblatt>(this.url(projectId, id));
  }

  create(projectId: string, request: TestdatenblattCreateRequest): Observable<Testdatenblatt> {
    return this.http.post<Testdatenblatt>(this.url(projectId), request);
  }

  update(projectId: string, id: string, request: TestdatenblattUpdateRequest): Observable<Testdatenblatt> {
    return this.http.put<Testdatenblatt>(this.url(projectId, id), request);
  }

  delete(projectId: string, id: string): Observable<void> {
    return this.http.delete<void>(this.url(projectId, id));
  }

  linkModule(projectId: string, id: string, moduleId: string): Observable<void> {
    return this.http.put<void>(this.moduleLinkUrl(projectId, id, moduleId), null);
  }

  unlinkModule(projectId: string, id: string, moduleId: string): Observable<void> {
    return this.http.delete<void>(this.moduleLinkUrl(projectId, id, moduleId));
  }

  getActivity(projectId: string, id: string): Observable<TestdatenblattActivity[]> {
    return this.http.get<TestdatenblattActivity[]>(`${this.url(projectId, id)}/activity`);
  }

  getUsage(projectId: string, id: string): Observable<TestdatenblattUsage> {
    return this.http.get<TestdatenblattUsage>(`${this.url(projectId, id)}/usage`);
  }

  getHandovers(projectId: string, id: string): Observable<HandoverChain> {
    return this.http.get<HandoverChain>(this.handoverUrl(projectId, id));
  }

  handOff(projectId: string, id: string, request: HandOffRequest): Observable<HandoverChain> {
    return this.http.post<HandoverChain>(this.handoverUrl(projectId, id), request);
  }

  closeHandover(projectId: string, id: string): Observable<HandoverChain> {
    return this.http.patch<HandoverChain>(`${this.handoverUrl(projectId, id)}/close`, {});
  }

  reopenHandover(projectId: string, id: string): Observable<HandoverChain> {
    return this.http.patch<HandoverChain>(`${this.handoverUrl(projectId, id)}/reopen`, {});
  }

  skipHandover(projectId: string, id: string, handoverId: string): Observable<HandoverChain> {
    return this.http.patch<HandoverChain>(`${this.handoverUrl(projectId, id)}/${handoverId}/skip`, {});
  }

  private handoverUrl(projectId: string, id: string): string {
    return `${this.url(projectId, id)}/handovers`;
  }

  private moduleLinkUrl(projectId: string, id: string, moduleId: string): string {
    return `${this.url(projectId, id)}/test-modules/${moduleId}`;
  }
}
